import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTheme } from "@emotion/react";
import OutOfRadiusDialog from "./OutOfRadiusDialog";
import ApprovalDialog from "./ApprovalDialogBox";
import {
  isUserIdKioskAuthMode,
  syncStoredKioskAuthMode,
} from "../utils/kioskAuthMode";

const EmployeesBox = ({
  handleEmployee,
  resetSignal,
  singleCard = false,
  headerContent = null,
  footerContent = null,
  className = "",
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [showUserId, setShowUserId] = useState(false);
  const [employeesList, setEmployeesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [isGeoOut, setIsGeoOut] = useState(false);
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [allowedLatLong, setAllowedLatLong] = useState();
  const [allowedRadius, setAllowedRadius] = useState();
  const [distance, setDistance] = useState();
  const [requestSent, setRequestSent] = useState(false);
  const [statusCode, setStatusCode] = useState(null);

  const theme = useTheme();
  const userLogin = useSelector((state) => state.userLogin);
  const { companyInfo } = userLogin;
  const isUserIdEmployeeLogin = isUserIdKioskAuthMode(companyInfo?.kioskAuthMode);

  const sortEmployees = (employees) => {
    return [...employees].sort((a, b) => {
      const aName = (a.employeeDetail?.fname || "").trim().toLowerCase();
      const bName = (b.employeeDetail?.fname || "").trim().toLowerCase();
      return aName.localeCompare(bName);
    });
  };

  const normalizeSearchText = (value) => (value || "").trim().toLowerCase();

  const getEmployeeSearchFields = (employee) => {
    const fname = normalizeSearchText(employee?.employeeDetail?.fname);
    const lname = normalizeSearchText(employee?.employeeDetail?.lname);
    const fullName = `${fname} ${lname}`.trim();
    const email = normalizeSearchText(employee?.email);

    return [fname, lname, fullName, email].filter(Boolean);
  };

  const getEmployeeName = (employee) => {
    if (!employee) {
      return "";
    }

    const fname = (employee.employeeDetail?.fname || "").trim();
    const lname = (employee.employeeDetail?.lname || "").trim();
    const email = (employee.email || "").trim();

    if (fname && lname) {
      return `${fname} ${lname}`;
    }
    if (fname) {
      return fname;
    }
    if (lname) {
      return lname;
    }
    if (email) {
      return email;
    }

    return "Unknown";
  };

  useEffect(() => {
    if (!companyInfo?.JWT_TOKEN) {
      return;
    }

    const fetchEmployees = (latitude, longitude) => {
      setLoading(true);

      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${companyInfo.JWT_TOKEN}`,
          Lat: latitude,
          Lon: longitude,
        },
      };

      axios
        .get(`${process.env.REACT_APP_API_BACKEND}/api/kiosk/dashboard`, config)
        .then((res) => {
          if (res.data.requireApproval === true && !localStorage.getItem("kioskRequestData")) {
            localStorage.setItem("kioskRequestData", JSON.stringify(res.data.kioskRequest));
          }

          const authModeChanged = syncStoredKioskAuthMode(res.data.kioskAuthMode);
          if (authModeChanged) {
            window.location.reload();
            return;
          }

          setEmployeesList(sortEmployees(res.data.employees || []));
          sessionStorage.setItem("showLogout", true);
          setLoading(false);
        })
        .catch((err) => {
          if (err.response?.data?.code === "inactive") {
            setError(err.response.data.message);
          } else if (err.response?.data?.code === "outOfRadius") {
            setError(err.response.data.message);
            setAllowedLatLong(err.response.data.allowedLatLong);
            setAllowedRadius(err.response.data.allowedRadius);
            setDistance(err.response.data.distance);
            setIsGeoOut(true);
          } else if (err.response?.data?.code === "noname") {
            setStatusCode("noname");
            setShowApprovalDialog(true);
          } else if (err.response?.data?.code === "pending") {
            setStatusCode("pending");
            setRequestSent(true);
            setShowApprovalDialog(true);
          } else if (err.response?.data?.code === "rejected") {
            setStatusCode("rejected");
            setRequestSent(true);
            setShowApprovalDialog(true);
          } else {
            setError(err.response?.data?.message || "Something went wrong");
            setTimeout(() => {
              localStorage.removeItem("userInfo");
              localStorage.removeItem("companyInfo");
              window.location.reload();
            }, 5000);
          }

          setLoading(false);
        });
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          setLat(latitude);
          setLon(longitude);
          fetchEmployees(latitude, longitude);
        },
        () => {
          fetchEmployees(null, null);
        },
        { enableHighAccuracy: true }
      );
    } else {
      fetchEmployees(null, null);
    }
  }, [companyInfo?.JWT_TOKEN]);

  useEffect(() => {
    setSelectedEmployee(null);
    setInputValue("");
    setShowUserId(false);
    handleEmployee(null);
  }, [resetSignal, handleEmployee]);

  const canShowOptions = inputValue.trim().length >= 3;
  const shouldShowPromptText = !isUserIdEmployeeLogin || !headerContent;

  return (
    <div>
      <Grid>
        {error && (
          <div
            style={{
              position: "absolute",
              top: "0",
              bottom: "0",
              left: "0",
              right: "0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: "100",
            }}
          >
            <Alert severity="error">{error}</Alert>
          </div>
        )}

        <Grid item xs={12}>
          <Paper
            square={false}
            className={[className, "employee-box"].filter(Boolean).join(" ")}
            style={{
              minHeight: singleCard ? "auto" : "70vh",
              padding: singleCard ? "2rem 1.5rem" : "1.25rem",
              borderRadius: "16px",
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              maxWidth: singleCard ? "720px" : undefined,
              margin: singleCard ? "0 auto" : undefined,
            }}
          >
            <Box sx={{ width: "100%", maxWidth: singleCard ? 520 : 560 }}>
              {headerContent && (
                <Box sx={{ mb: 3 }}>
                  {headerContent}
                </Box>
              )}

              {shouldShowPromptText && (
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: 600, textAlign: "center" }}
                >
                  {isUserIdEmployeeLogin ? "Enter your kiosk User ID" : "Search your name below"}
                </Typography>
              )}

              {isUserIdEmployeeLogin ? (
                <TextField
                  fullWidth
                  label="Kiosk User ID"
                  placeholder="Enter your kiosk User ID"
                  type={showUserId ? "text" : "password"}
                  value={inputValue}
                  autoComplete="new-password"
                  onChange={(event) => {
                    const newInputValue = event.target.value;
                    setInputValue(newInputValue);
                    setSelectedEmployee(null);
                    handleEmployee(
                      newInputValue.trim()
                        ? { kioskUserId: newInputValue.trim(), loginMode: "userId" }
                        : null
                    );
                  }}
                  InputLabelProps={{
                    sx: {
                      color: theme.palette.text.primary,
                      "&.Mui-focused": {
                        color: theme.palette.text.primary,
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showUserId ? "Hide kiosk User ID" : "Show kiosk User ID"}
                          edge="end"
                          onClick={() => setShowUserId((prev) => !prev)}
                        >
                          {showUserId ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      fontSize: singleCard ? "1.1rem" : "1rem",
                    },
                  }}
                />
              ) : (
                <Autocomplete
                  options={canShowOptions ? employeesList : []}
                  loading={loading}
                  value={selectedEmployee}
                  inputValue={inputValue}
                  clearOnBlur={false}
                  noOptionsText={canShowOptions ? "No matching employee" : "Type at least 3 characters"}
                  filterOptions={(options, state) => {
                    const query = normalizeSearchText(state.inputValue);

                    if (query.length < 3) {
                      return [];
                    }

                    return options.filter((employee) =>
                      getEmployeeSearchFields(employee).some((field) => field.includes(query))
                    );
                  }}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  onChange={(event, selected) => {
                    setSelectedEmployee(selected || null);
                    handleEmployee(selected || null);
                  }}
                  getOptionLabel={(option) => getEmployeeName(option)}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search employee"
                      placeholder="Type employee name"
                      size="small"
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        sx: {
                          color: theme.palette.text.primary,
                          "&.Mui-focused": {
                            color: theme.palette.text.primary,
                          },
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiAutocomplete-clearIndicator": {
                      visibility: selectedEmployee ? "visible" : "hidden",
                      opacity: selectedEmployee ? 1 : 0,
                    },
                    "& .MuiAutocomplete-clearIndicator:hover": {
                      opacity: 1,
                    },
                    "& .MuiAutocomplete-popupIndicator": {
                      display: canShowOptions ? "inline-flex" : "none",
                    },
                  }}
                />
              )}

              {!isUserIdEmployeeLogin && !canShowOptions && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.75,
                    display: "block",
                    color: theme.palette.text.primary,
                    opacity: 0.8,
                    textAlign: "left",
                  }}
                >
                  Minimum 3 letters required
                </Typography>
              )}

              <Box
                sx={{
                  height: 42,
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {loading && <CircularProgress size={24} />}
              </Box>

              {footerContent && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 3,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {footerContent}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {isGeoOut && (
        <OutOfRadiusDialog
          open={isGeoOut}
          onClose={() => setIsGeoOut(false)}
          currLat={lat}
          currLong={lon}
          allowedLat={allowedLatLong.lat}
          allowedLong={allowedLatLong.lon}
          allowedRadius={allowedRadius}
          distance={distance}
        />
      )}

      <ApprovalDialog
        open={showApprovalDialog}
        requestSent={requestSent}
        setRequestSent={setRequestSent}
        statusCode={statusCode}
      />
    </div>
  );
};

export default EmployeesBox;
